import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { Subscription } from 'rxjs'
import { AymakanSettings, AymakanSettingsService } from './aymakan-settings.service'
import { getCityOptions, getCountryOptions, MAJOR_SA_CITIES } from '../shared/origin-location.util'

const AYMAKAN_CREDENTIAL_FIELDS: (keyof AymakanSettings)[] = ['apiKey']

@Component({
  selector: 'app-aymakan-provider',
  templateUrl: './aymakan-provider.component.html',
  styleUrls: ['../../../integrations/integrations.component.scss', '../shipping-providers-theme.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AymakanProviderComponent implements OnInit, OnDestroy {
  @Input() projectId!: string
  @Input() isOpen = false
  @Output() openPanel = new EventEmitter<void>()
  @Output() closePanel = new EventEmitter<void>()
  @Output() saved = new EventEmitter<void>()

  rateMode: 'flat' | 'weight' = 'flat'

  settings: AymakanSettings = {
    enabled: false,
    apiKey: '',
    originCountryCode: 'SA',
    originCityName: '',
    originAddressLine: '',
    originPhone: '',
    flatRate: null,
    baseRate: null,
    perKgRate: null,
  }

  // Mirrors AYMAKAN_SUPPORTED_COUNTRIES in stacks-agents'
  // src/app/shipping/providers/aymakan/aymakan.provider.ts.
  countries = getCountryOptions(['SA'])

  originCities: string[] = []

  private statusSub?: Subscription

  constructor(public readonly service: AymakanSettingsService) {
    // originCountryCode defaults to 'SA' above, so the city list must be
    // seeded immediately — not just after an existing connection loads.
    this.refreshOriginCities()
  }

  ngOnInit(): void {
    this.statusSub = this.service.status$.subscribe((status) => {
      if (!status?.connected) return
      this.settings.enabled = status.enabled
      this.settings.originCountryCode = status.originCountryCode ?? this.settings.originCountryCode
      this.settings.originCityName = status.originCityName ?? this.settings.originCityName
      this.settings.originAddressLine = status.originAddressLine ?? this.settings.originAddressLine
      this.settings.originPhone = status.originPhone ?? this.settings.originPhone
      this.settings.flatRate = status.flatRate ?? null
      this.settings.baseRate = status.baseRate ?? null
      this.settings.perKgRate = status.perKgRate ?? null
      this.rateMode = status.perKgRate != null ? 'weight' : 'flat'
      this.service.maskCredentials(this.settings, AYMAKAN_CREDENTIAL_FIELDS)
      this.refreshOriginCities()
    })
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe()
  }

  /**
   * Repopulates the Origin City options for the currently selected origin
   * country. Keeps an already-saved city selected even if it isn't in the
   * list, so switching countries never silently drops existing data.
   */
  onOriginCountryChange(): void {
    this.settings.originCityName = ''
    this.refreshOriginCities()
  }

  private refreshOriginCities(): void {
    const countryIsoCode = this.settings.originCountryCode
    const curated = countryIsoCode === 'SA' ? MAJOR_SA_CITIES : undefined
    this.originCities = getCityOptions(countryIsoCode, this.settings.originCityName, curated)
  }

  /**
   * Saves encrypted Aymakan configuration through stacks-agents.
   * Strips placeholder values so the backend preserves existing credentials.
   */
  async save(): Promise<void> {
    const settingsToSave = { ...this.settings }
    for (const field of AYMAKAN_CREDENTIAL_FIELDS) {
      if (settingsToSave[field] === this.service.CREDENTIAL_PLACEHOLDER) {
        ;(settingsToSave as Record<string, unknown>)[field] = ''
      }
    }
    await this.service.save(this.projectId, settingsToSave)
    this.saved.emit()
  }

  /**
   * Removes the Aymakan integration through stacks-agents while keeping
   * credential handling inside the backend.
   */
  disconnect(): void {
    this.service.disconnect(this.projectId)
  }
}
